import _ from 'underscore';

import {assert} from 'Asserts';
import * as EventConstants from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import AppDispatcher from 'lib/Dispatcher';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import APIUtil from 'util/APIUtil';
import * as StageEntryTemplateActions from 'workflows/entry-templates/StageEntryTemplateActions';
import WorkflowEntryTemplateStore from 'workflows/entry-templates/WorkflowEntryTemplateStore';
import * as WorkflowEntryTemplateUtil from 'workflows/entry-templates/WorkflowEntryTemplateUtil';

/**
 * Actions that are applicable for StageEntryTemplates.
 */
export function fetchTemplateVersion(entryTemplateId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_FETCH_STARTED,
    entryTemplateId,
  });
  const onSuccess = (entryTemplate: WorkflowEntryTemplateUtil.WorkflowEntryTemplate): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_FETCH_COMPLETED,
      entryTemplateId,
      entryTemplate,
    });
  };
  const onFailure = (error: Response): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_FETCH_FAILED,
      entryTemplateId,
      error,
    });
  };
  assert(
    APIUtil.apiIdToType(entryTemplateId) === ModelTypeConstants.STAGE_CONFIGURATION,
    'Tried to fetch template version for something that is not a stage configuration.'
  );
  StageEntryTemplateActions.fetch(entryTemplateId, {onSuccess, onFailure});
}

export function discardEntryTemplate(entryTemplateId: string): void {
  if (entryTemplateId != null && WorkflowEntryTemplateStore.inStore(entryTemplateId)) {
    AppDispatcher.dispatch({
      actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_DISCARD,
      entryTemplateId,
    });
  }
}

export function registerObjects(entryTemplateId: string, cidIdMap: {[cid: string]: string}): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_REGISTER_OBJECTS,
    entryTemplateId,
    cidIdMap,
  });
}

function save(entryTemplateId: string, {onSuccess}: {onSuccess: () => void}): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_SAVE_STARTED,
    entryTemplateId,
  });

  const {entryTemplate, openVersion, metadata} = WorkflowEntryTemplateStore.getEditorState(entryTemplateId);

  // eslint-disable-next-line no-restricted-syntax
  const serverEntryTemplate = _.extend(
    WorkflowEntryTemplateStore.prepareForSave(entryTemplateId, entryTemplate),
    {metadata}
  );

  const onSaveSuccess = (cidIdMap: {[cid: string]: string}): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_SAVE_COMPLETED,
      entryTemplateId,
      savedVersion: openVersion,
      versionId: cidIdMap[serverEntryTemplate.cid],
      cidIdMap,
    });
    onSuccess();
  };
  const onFailure = (error: Response): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_SAVE_FAILED,
      entryTemplateId,
      error,
    });
  };

  assert(
    APIUtil.apiIdToType(entryTemplateId) === ModelTypeConstants.STAGE_CONFIGURATION,
    'Tried to save workflow entry template but found a different model type.'
  );
  StageEntryTemplateActions.save(entryTemplateId, serverEntryTemplate, {
    onSuccess: onSaveSuccess,
    onFailure,
  });
}

export function updateNote(
  entryTemplateId: string,
  note: EntryNote,
  name: string,
  {bumpVersion = true}: Partial<{bumpVersion: boolean}> = {}
): void {
  const updateOperation = {
    name: {$set: name},
    ...WorkflowEntryTemplateUtil.entryVersionUpdate({$merge: {note}}),
  };
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_ENTRY_TEMPLATE_UPDATE,
    entryTemplateId,
    op: updateOperation,
    bumpVersion,
  });
}

export function updateAndSaveEntryTemplate(
  entryTemplateId: string,
  note: EntryNote,
  name: string,
  {onSuccess}: {onSuccess: () => void}
): void {
  updateNote(entryTemplateId, note, name);
  save(entryTemplateId, {onSuccess});
}
